import { Box, Grid2, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { parseDateToLgComun4 } from "../../../helpers/dateTransform";

// Los roles son filas creadas por cada tenant, no constantes del codigo, asi que
// el mapeo vive aqui para poder ajustarlo sin tocar el render.
// Fuente: role_name de /api/report/assigned-users-from-report/
export const ROLE_MAP = {
  investigator: ["Investigador", "Investigador CSIS"],
  manager: ["Manager / Lead", "Manager CSIS", "Manager Intake CSIS"],
  analyst: ["Analista Investigación", "Analista Intake CSIS"],
  ethics_office: ["Officers y Coordinadores", "Director OE"],
};

const normalize = (value) => String(value ?? "").trim().toLowerCase();

// Nombres de los usuarios asignados cuyo rol cae en el grupo pedido
export const getUsersByRole = (assignedUsers, group) => {
  const wanted = (ROLE_MAP[group] ?? []).map(normalize);
  const users = Array.isArray(assignedUsers) ? assignedUsers : [];
  return users
    .filter((user) => wanted.includes(normalize(user?.role_name)))
    .map((user) => user?.name || user?.email)
    .filter(Boolean)
    .join(", ");
};

// No existe un campo de "fecha de asignacion a investigacion": se deriva del
// historial de estatus, buscando cuando el reporte entro a "process"
// (process = INVESTIGACION, segun el mapeo de estatus del backend).
export const getInvestigationAssignmentDate = (status) => {
  const history = Array.isArray(status?.history) ? status.history : [];
  const entry = history.find((item) => item?.status === "process");
  if (entry?.assigned) return entry.assigned;
  // Si sigue en investigacion y el historial aun no lo registro, va el actual
  if (status?.current === "process" && status?.assigned) return status.assigned;
  return null;
};

// Renglon etiqueta / valor del resumen
const SummaryRow = ({ label, value, size = { xs: 12, sm: 6 } }) => {
  const theme = useTheme();
  return (
    <Grid2 size={size}>
      <Typography fontSize={11} fontWeight={600} color="secondary">
        {label}
      </Typography>
      <Typography
        fontSize={13}
        component="div"
        color={theme.palette.text.primary}
        borderBottom={`1px solid ${theme.palette.blueGray200.main}`}
        sx={{ minHeight: 20, wordBreak: "break-word" }}
      >
        {value || ""}
      </Typography>
    </Grid2>
  );
};

// Ventana "resumen" del reporte
export const ReportSummary = ({ reportDetail, assignedUsers, trackingCode }) => {
  const [t] = useTranslation("information");
  const theme = useTheme();

  const formatDate = (date) =>
    date ? parseDateToLgComun4(date, t("information.locale")) : "";

  return (
    <Box
      className="rounded dyTheme2"
      mt={1.7}
      sx={{
        border: `1px solid ${theme.palette.blueGray200.main}`,
        px: 1.5,
        py: 1.2,
      }}
    >
      <Typography variant="h6" color="primary" fontSize={9} mb={1}>
        {`${t("information.summary_title")}:`}
      </Typography>

      <Grid2 container spacing={1.5} alignItems="flex-start">
        <SummaryRow
          size={12}
          label={t("information.summary_description")}
          value={reportDetail?.description}
        />

        <SummaryRow
          label={t("information.summary_investigator")}
          value={getUsersByRole(assignedUsers, "investigator")}
        />
        <SummaryRow
          label={t("information.summary_manager")}
          value={getUsersByRole(assignedUsers, "manager")}
        />
        <SummaryRow
          label={t("information.summary_analyst")}
          value={getUsersByRole(assignedUsers, "analyst")}
        />
        <SummaryRow
          label={t("information.summary_ethics_office")}
          value={getUsersByRole(assignedUsers, "ethics_office")}
        />

        <SummaryRow
          label={t("information.summary_registered_at")}
          value={formatDate(reportDetail?.created_at)}
        />
        <SummaryRow
          label={t("information.summary_investigation_assigned_at")}
          value={formatDate(getInvestigationAssignmentDate(reportDetail?.status))}
        />

        {/* PENDIENTE BACKEND: no existe un campo estructurado con la fecha del
            hecho; si el formulario la pregunta, hoy solo vive dentro de stepers. */}
        <SummaryRow
          label={t("information.summary_situation_reported_at")}
          value=""
        />
        {/* PENDIENTE BACKEND: no existe ningun campo de impacto financiero. */}
        <SummaryRow
          label={t("information.summary_financial_impact")}
          value=""
        />

        <SummaryRow
          label={t("information.summary_tracking_code")}
          value={trackingCode}
        />
      </Grid2>
    </Box>
  );
};
